import type { ReactNode } from 'react';
import type { DocumentAuditAction, DocumentDownloadLog } from '../../api/downloadLogs';
import { formatProjectDateTime } from '../../utils/formatProjectDateTime';
import { LoadingSpinner } from '../LoadingSpinner';

interface DownloadLogTableProps {
  scope: 'project' | 'global';
  logs: DocumentDownloadLog[];
  loading: boolean;
}

interface Column {
  key: string;
  header: string;
  render: (log: DocumentDownloadLog) => ReactNode;
}

const formatFileSize = (bytes: number | null): string => {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ACTION_LABELS: Record<DocumentAuditAction, string> = {
  download: 'Downloaded',
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
};

/**
 * Plain table, no client-side sort — the backend's `created_at DESC` order is
 * fixed and not configurable per the API contract. Covers the full document
 * audit trail (download/created/updated/deleted), not just downloads.
 */
export const DownloadLogTable = ({ scope, logs, loading }: DownloadLogTableProps) => {
  if (loading) {
    return <LoadingSpinner text="Loading log..." className="py-12" />;
  }

  if (logs.length === 0) {
    return <div className="py-12 text-center text-[#888]">No log entries found for the selected filters.</div>;
  }

  const columns: Column[] = [
    { key: 'user', header: 'User', render: log => log.user_name || log.user_email || '—' },
    { key: 'action', header: 'Action', render: log => ACTION_LABELS[log.action] || log.action },
    { key: 'document', header: 'Document', render: log => log.document_name },
    ...(scope === 'global'
      ? [{ key: 'project', header: 'Project', render: (log: DocumentDownloadLog) => log.project_name || '—' }]
      : []),
    { key: 'type', header: 'Type', render: log => log.file_type_label },
    { key: 'size', header: 'Size', render: log => formatFileSize(log.file_size) },
    { key: 'when', header: 'When', render: log => formatProjectDateTime(log.created_at, log.timezone) },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#eaeaea] text-left text-[#888]">
            {columns.map(c => (
              <th key={c.key} className="px-4 py-2 font-medium">{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {logs.map(log => (
            <tr key={log.id} className="border-b border-[#f0f0f0] text-[#171717]">
              {columns.map(c => (
                <td key={c.key} className="px-4 py-2">{c.render(log)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
